/**
 * Single source of truth for tenant/applicant spoken languages.
 *
 * - Store ISO-639-1 codes in the DB/payload (e.g. ["de","en","tr"]).
 * - Display German labels via code -> label lookup.
 * - Display order is ALWAYS the LANGUAGE_OPTIONS order, never click/save order.
 * - Unknown codes (e.g. free-text written by a future backend path) fall back
 *   to showing the code itself — never crash, never an empty entry.
 *
 * Order: Zürich-frequency first (de/en/fr/it/es/pt), then
 * West -> Central -> South-East -> East -> North Europe, then Turkish/Arabic
 * as important non-European applicant languages.
 */

// Grouped for a scannable checkbox UI. Concatenated in order, the groups
// produce exactly the flat LANGUAGE_OPTIONS order below.
export const LANGUAGE_GROUPS = [
  {
    title: "Häufig",
    options: [
      { code: "de", label: "Deutsch" },
      { code: "en", label: "Englisch" },
      { code: "fr", label: "Französisch" },
      { code: "it", label: "Italienisch" },
      { code: "es", label: "Spanisch" },
      { code: "pt", label: "Portugiesisch" },
    ],
  },
  {
    title: "West-/Mitteleuropa",
    options: [
      { code: "nl", label: "Niederländisch" },
      { code: "pl", label: "Polnisch" },
      { code: "cs", label: "Tschechisch" },
      { code: "sk", label: "Slowakisch" },
      { code: "hu", label: "Ungarisch" },
    ],
  },
  {
    title: "Südosteuropa",
    options: [
      { code: "ro", label: "Rumänisch" },
      { code: "bg", label: "Bulgarisch" },
      { code: "hr", label: "Kroatisch" },
      { code: "sr", label: "Serbisch" },
      { code: "bs", label: "Bosnisch" },
      { code: "sl", label: "Slowenisch" },
      { code: "sq", label: "Albanisch" },
      { code: "el", label: "Griechisch" },
    ],
  },
  {
    title: "Osteuropa",
    options: [
      { code: "ru", label: "Russisch" },
      { code: "uk", label: "Ukrainisch" },
    ],
  },
  {
    title: "Nordeuropa",
    options: [
      { code: "sv", label: "Schwedisch" },
      { code: "no", label: "Norwegisch" },
      { code: "da", label: "Dänisch" },
      { code: "fi", label: "Finnisch" },
    ],
  },
  {
    title: "Weitere",
    options: [
      { code: "tr", label: "Türkisch" },
      { code: "ar", label: "Arabisch" },
    ],
  },
];

// Flat, ordered list — the canonical order for display + storage.
export const LANGUAGE_OPTIONS = LANGUAGE_GROUPS.flatMap((g) => g.options);

const LABEL_BY_CODE = new Map(LANGUAGE_OPTIONS.map((o) => [o.code, o.label]));
const ORDER_BY_CODE = new Map(LANGUAGE_OPTIONS.map((o, i) => [o.code, i]));

/** Label for a code, or the code itself as fallback for unknown codes. */
export function languageLabel(code) {
  return LABEL_BY_CODE.get(code) || code;
}

/** Sort a list of codes by the canonical LANGUAGE_OPTIONS order.
 *  Unknown codes keep their relative order and are appended after known ones. */
function sortByCanonicalOrder(codes) {
  const known = [];
  const unknown = [];
  for (const c of codes) {
    if (ORDER_BY_CODE.has(c)) known.push(c);
    else unknown.push(c);
  }
  known.sort((a, b) => ORDER_BY_CODE.get(a) - ORDER_BY_CODE.get(b));
  return [...known, ...unknown];
}

/** Full display string: labels in canonical order, joined with ", ". Empty -> "—". */
export function formatLanguages(codes) {
  if (!Array.isArray(codes) || codes.length === 0) return "—";
  return sortByCanonicalOrder(codes).map(languageLabel).join(", ");
}

/** Compact display for narrow table cells: uppercase codes in canonical order,
 *  truncated to `max` with a "+N" suffix. Empty -> "—". */
export function formatLanguagesShort(codes, max = 3) {
  if (!Array.isArray(codes) || codes.length === 0) return "—";
  const ordered = sortByCanonicalOrder(codes);
  const shown = ordered.slice(0, max).map((c) => c.toUpperCase());
  const rest = ordered.length - shown.length;
  return rest > 0 ? `${shown.join(", ")} +${rest}` : shown.join(", ");
}
